'use client'

import { useEffect, useState, useCallback, useMemo } from 'react'
import dynamic from 'next/dynamic'
import { datadogLogs } from '@datadog/browser-logs'
import { useRouter } from 'next/navigation'
import { RightSidebarContainer } from 'src/components/RightSidebar'
import { useGetIsSocialFocusOpen, useGetIsTracksPanelOpen, useUIActions } from 'src/store/ui'
import { isAnonymousPlayer, isAuthenticatedPlayer } from 'src/types/domain/Player'
import { isMarbleGame } from 'src/types/domain/Game'
import { GameTopToolbarContainer } from 'src/components/GameTopToolbar'
import { ShowRaceBannerContainer } from 'src/components/ShowRaceBanner'
import { CurrentBetPanelContainer } from 'src/components/CurrentBetPanel'
import { PlaceBetPanel } from 'src/components/PlaceBetPanel'
import { useOneTimeLogin } from 'src/hooks/useOneTimeLogin'
import { useGameListeners } from 'src/hooks/useGameListeners'
import { AnonymousPlayerView } from 'src/components/AnonymousPlayerView'
import {
  useIsGameAccessRestricted,
  useAreAllowedGamesFetched,
  useGetCurrentGame,
  useGetCurrentLanguage,
  useGetGameConfig
} from 'src/store/game'
import { useGetCurrentPlayer, useGetCurrentPlayerId } from 'src/store/player'
import { useClientSideTranslation } from 'src/services/i18n'
import { useHasFatalError } from 'src/store/error'
import ActionsPane from 'src/components/CurrentBetPanel/components/ActionsPane'
import { StreamRoundIntro } from 'src/components/StreamRoundIntro'
import { MobileBoostIndicator } from 'src/components/MobileBoostIndicator'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { OrientationMessage } from 'src/components/OrientationMessage'
import { getLatestGameEvent } from 'src/utils/raceUtils'
import { useGetCurrentBet } from 'src/hooks/useGetCurrentBet'
import { transformPresets, useViewportScale } from 'src/hooks/useViewportScale'
import { useSyncWithRetry } from 'src/hooks/useSyncWithRetry'
import { AnimatedSocialBetStream, SocialBetTable, MarbleBetTable } from 'src/components/Social'
import { useGetSocialBetsGameId } from 'src/store/social'
import { useGameAPI } from 'src/hooks/useGameAPI'
import { GameTracksPanel } from 'src/components/GameTracksPanel'
import { useGames } from 'src/hooks/useGames'
import { LoadingIndicator } from 'src/components/LoadingIndicator'
import { ErrorView } from 'src/components/ErrorView'
import { ShowRaceResults } from 'src/components/ShowRaceResults'
import { ShowRaceStatus } from 'src/components/ShowRaceStatus'
import {
  CrashChipPanel,
  CrashHudOverlay,
  CrashMultiplierDisplay,
  CrashPotentialPill,
  type CrashPanelStats
} from 'src/components/CrashChipPanel'
import { resolvePayoutCeiling } from 'src/components/CrashChipPanel/crashPayoutCeiling'
import { fromInteger } from '@repo/money'
import { ChipPlacementPanel } from 'src/components/ChipPlacementPanel'
import {
  BIRDIE_ARC_SLOT_CLASS,
  BirdieBoard,
  boardFor,
  BirdieBallDefs
} from 'src/components/BirdieBoard'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import { playoutDelayForGame } from 'src/hooks/crashStreamConfig'
import { RoundStatsPopover } from 'src/components/RoundStatsPopover'
import {
  useGetIsPlacingNextRaceBet,
  useGetNextRaceBet,
  useNextRaceBetActions
} from 'src/store/nextRaceBet'
import { IdleBanner } from 'src/components/IdleBannerWrapper'
import { usePlayerBets } from 'src/hooks/usePlayerBets'
import { usePlayerBalance } from 'src/hooks/usePlayerBalance'
import { useGetPlayerChannel, useGetPlayerChannelJoinEpoch } from 'src/store/socket'
import { useDeviceInfo } from 'src/hooks/useDeviceInfo'
import { BoosterItem, BonusItem } from 'src/features/inventory'
import {
  useGetAutoBooster,
  useGetActiveBonus,
  useInventoryActions
} from 'src/features/inventory/store'
import { useStreakActions, useGetCurrentStreak } from 'src/store/streak'
import { displayAmount, displayMoney, getCurrencyDecimals } from 'src/utils/displayAmount'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { lobbyPath } from 'src/utils/lobbyPath'
import { useAnimatedNumber } from 'src/hooks/useAnimatedNumber'
import styles from '../assets/css/modules/show-broadcast.module.scss'
import { showSocial } from './_utils/showSocial'

const LiveStream = dynamic(() => import('src/components/LiveStream'), { ssr: false })

type Props = Readonly<{
  searchParams?: Record<string, string | undefined>
  params: { gameId: string }
}>

export default function GamePage({ searchParams, params }: Props) {
  // Get Game id from URL
  const currentGameId = params.gameId

  // Get optional OTL from URL
  useOneTimeLogin(searchParams?.otl)

  const router = useRouter()
  const currentPlayer = useGetCurrentPlayer()
  const currentPlayerId = useGetCurrentPlayerId()
  const gameConfig = useGetGameConfig()
  const socialBetsGameId = useGetSocialBetsGameId()
  const isTracksPanelOpen = useGetIsTracksPanelOpen()
  // v17 — the shared mobile-crash swap flag. When open, the social panel fills
  // the top region and the REAL <video> (mounted below) shrinks via CSS into
  // the top-right corner square. The <video> never re-mounts — only a class on
  // its container (.dolby-feed) toggles, so the live stream is never
  // reconnected/flickered.
  const isSocialFocusOpen = useGetIsSocialFocusOpen()
  const areAllowedGamesFetched = useAreAllowedGamesFetched()
  const isGameAccessRestricted = useIsGameAccessRestricted(currentGameId)
  const isPlacingNextRaceBet = useGetIsPlacingNextRaceBet()
  const nextRaceBet = useGetNextRaceBet()

  const { fetchBets } = usePlayerBets()
  const { updateBalance } = usePlayerBalance()
  const playerChannel = useGetPlayerChannel()
  const playerChannelJoinEpoch = useGetPlayerChannelJoinEpoch()
  const { fetchCurrentGame } = useGameAPI()
  const currentGame = useGetCurrentGame()
  const { fetchPlayableGames } = useGames()
  const oldestBooster = useGetAutoBooster(isMarbleGame(currentGame) ? currentGame.id : undefined)
  const activeBonus = useGetActiveBonus(isMarbleGame(currentGame) ? currentGame.id : undefined)
  const { deactivateItem } = useInventoryActions()
  const streakActions = useStreakActions()
  const currentStreak = useGetCurrentStreak()
  const hasFatalError = useHasFatalError()

  const { currentBet } = useGetCurrentBet()
  const userHasBet = currentBet !== undefined
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const latestEvent = getLatestGameEvent(currentGame)
  const [wasScheduledRace, setWasScheduledRace] = useState(false)
  const [isBettingAllowed, setIsBettingAllowed] = useState<boolean>(false)

  const [chipStats, setChipStats] = useState<{
    totalStaked: number
    totalPotentialWin: number
    chipCount: number
  }>({ totalStaked: 0, totalPotentialWin: 0, chipCount: 0 })

  // Crash panel stats for the same bottom-left overlay (crash games hide the
  // top-right header balance like chip-placement games do — see
  // BalanceContainer). `totalStaked` = placed/queued stakes; `inPlay` =
  // submitted and still riding the current round.
  const [crashStats, setCrashStats] = useState<CrashPanelStats>({
    totalStaked: 0,
    inPlay: 0,
    potential: { kind: 'idle' }
  })

  // Smoothly tween the three chip-stats pills between renders so the
  // numbers count down/up rather than snapping.
  //   - Balance: counts DOWN when bets are placed, UP when bets win.
  //   - Total Bet: counts UP as chips are added, DOWN on trash/undo.
  //   - Potential Win: tracks total-bet edits the same way.
  // Hooks are always invoked (with 0 when the player is anonymous) so
  // the call order stays stable across renders.
  const balanceTarget = isAuthenticatedPlayer(currentPlayer) ? currentPlayer.balance.amount : 0
  const balanceCurrency = isAuthenticatedPlayer(currentPlayer)
    ? currentPlayer.balance.currency
    : 'USD'
  // Operator liability ceiling in DECIMAL units. The crash multiplier is
  // uncapped but the payout is not — the backend clamps every cash-out to this
  // — so the Potential Win pill's riding projection freezes here instead of
  // counting past a figure the server will refuse. maxPayout arrives in INTEGER
  // wire units; resolvePayoutCeiling owns that conversion.
  const crashPayoutCeiling = useMemo(
    () =>
      resolvePayoutCeiling(gameConfig?.bet?.maxPayout, (value) =>
        fromInteger(value, balanceCurrency)
      ),
    [gameConfig, balanceCurrency]
  )
  const animatedBalance = useAnimatedNumber(balanceTarget)
  const animatedTotalStaked = useAnimatedNumber(chipStats.totalStaked)
  const animatedTotalPotentialWin = useAnimatedNumber(chipStats.totalPotentialWin)
  const animatedCrashStaked = useAnimatedNumber(crashStats.totalStaked)
  const animatedCrashInPlay = useAnimatedNumber(crashStats.inPlay)

  const { isLandscape, isMobile } = useDeviceInfo()
  const bottomStyle = useViewportScale({
    transform: transformPresets.bottomPanel
  })

  const rightStyle = useViewportScale({
    transform: transformPresets.rightFloatingPanel
  })

  // Bottom-left chip-placement stats overlay (BALANCE / TOTAL BET / POTENTIAL
  // WIN). Scales the same way as the other floating panels when the window
  // shrinks below the ideal width, but stays anchored to the bottom-left
  // corner via `transformOrigin: 'bottom left'`. Mobile is opted out by the
  // hook itself — there the overlay uses its own full-width layout from
  // marbles.scss.
  const chipStatsStyle = useViewportScale({
    transform: transformPresets.basic,
    transformOrigin: 'bottom left'
  })

  const nextRaceBetActions = useNextRaceBetActions()
  const uiActions = useUIActions()

  const openRightSidebar = useCallback(
    (tab: 'betHistory' | 'pastRaces' | 'stats') => {
      uiActions.sidebarOpened(true)
      uiActions.tabActivated(tab)
    },
    [uiActions]
  )

  // State of the previous game is capt when using the browser's back button
  // This can lead to weird behaviours like not joining the correct game channel
  const isGameMismatch = isMarbleGame(currentGame) && currentGameId !== currentGame.id
  useGameListeners(isGameMismatch)

  useSyncWithRetry(() => fetchCurrentGame(currentGameId), currentGameId)

  useEffect(() => {
    setIsBettingAllowed(
      latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting'
    )
  }, [latestEvent])

  useEffect(() => {
    if (areAllowedGamesFetched && isMarbleGame(currentGame) && isGameAccessRestricted) {
      router.replace(lobbyPath())
    }
  }, [isGameAccessRestricted, currentGame, router, areAllowedGamesFetched])

  useEffect(() => {
    if (isMarbleGame(currentGame)) {
      if (latestEvent?.eventType === 'race_scheduled' && currentPlayerId) {
        uiActions.tracksPanelOpened(true)
        setWasScheduledRace(true)

        datadogLogs.logger.info('tracks_panel', {
          user_action: {
            type: 'open_scheduled',
            currentGame: currentGame.id,
            playerId: currentPlayerId
          }
        })
      } else if (latestEvent?.eventType !== 'race_scheduled' && wasScheduledRace) {
        // Only close the panel if we were previously on a scheduled race
        uiActions.tracksPanelOpened(false)
        setWasScheduledRace(false)

        datadogLogs.logger.info('tracks_panel', {
          user_action: {
            type: 'close_scheduled',
            currentGame: currentGame.id,
            playerId: currentPlayerId
          }
        })
      }
    }
  }, [currentGame, latestEvent, currentPlayerId, wasScheduledRace, uiActions])

  useEffect(() => {
    if (isMarbleGame(currentGame)) {
      // Clear next race bet if game has changed
      nextRaceBetActions.clearNextRaceBetIfGameChanged(currentGame.id)
    }
  }, [currentGame, nextRaceBetActions])

  // Clear streak when switching games (streaks are per-game)
  useEffect(() => {
    streakActions.clearStreak()
  }, [currentGameId, streakActions])

  // Player-channel (re)join resync. The join reply replays games/streaks/
  // inventory but NO bet states, and the player-channel pushes are
  // at-most-once — so anything that fired while the socket was down is gone.
  // `bet:cashed-out` is the costly one: lose it and the crash card keeps
  // climbing and busts on a bet the server already paid out.
  //
  // Keyed on the join EPOCH, not just the channel identity: phoenix-js rejoins
  // on its own timer and keeps the same Channel object, so identity alone
  // misses exactly the reconnects this exists for. The epoch and the channel
  // are written in one store update, so the initial join still runs this once.
  // Gated on 'joined' so the mid-rejoin render (channel cleared) is skipped
  // rather than firing two pushes at a channel that cannot answer them.
  useEffect(() => {
    if (!currentPlayerId) return
    if (playerChannel?.state !== 'joined') return
    void fetchBets()
    void updateBalance()
  }, [fetchBets, updateBalance, currentPlayerId, playerChannel, playerChannelJoinEpoch])

  useEffect(() => {
    void fetchPlayableGames(currentGameId)
  }, [areAllowedGamesFetched, fetchPlayableGames, currentGameId])

  // MUST stay above the early returns below — three of them (anonymous, fatal
  // error, loading) sit between here and the render, so declaring these any
  // lower changes the hook count between renders and React throws.
  const [isStatsOpen, setIsStatsOpen] = useState(false)
  const toggleStats = useCallback(() => setIsStatsOpen((open) => !open), [])

  if (isAnonymousPlayer(currentPlayer)) {
    return <AnonymousPlayerView />
  }

  if (hasFatalError) {
    return <ErrorView />
  }

  if (
    !isMarbleGame(currentGame) ||
    !isKnownGameConfig(gameConfig) ||
    !isAuthenticatedPlayer(currentPlayer) ||
    !areAllowedGamesFetched ||
    isGameAccessRestricted
  ) {
    return <LoadingIndicator />
  }

  // In mobile landscape we show OrientationMessage as an overlay and hide the
  // game tree, rather than early-returning it. Early-returning unmounted
  // ChipPlacementPanel, so rotating to landscape and back wiped the player's
  // placed (unsubmitted) chips and selected chip (QA-60129). Keeping it
  // mounted but hidden preserves all that transient state across rotation.
  const showOrientationMessage = isMobile && isLandscape

  // MilliCast Stream
  const streamAccountId = process.env.NEXT_PUBLIC_MILLICAST_STREAM_ACCOUNT_ID || ''
  const streamSubscriberToken = process.env.NEXT_PUBLIC_MILLICAST_SUBSCRIBER_TOKEN || ''

  const streamName = `master_${currentGame.track.name
    .toLowerCase()
    .replace(/\s+/g, '-')}_${currentGame.name.toLowerCase().replace(/\s+/g, '-')}-${currentGame.id}`

  const isScheduledRace = isMarbleGame(currentGame) && latestEvent?.eventType === 'race_scheduled'
  const shouldHideGameInterface = isTracksPanelOpen && isScheduledRace
  const shouldHideStream = shouldHideGameInterface && gameConfig.lobbyDisabled
  const isCrashGame = isMarbleGame(currentGame) && isCrashGameType(currentGame.track.gameType)
  // Which betting surface this game gets. Pure + tested (boardFor.test.ts) so
  // "a classic game never gets the Birdie board" is a guarantee, not a reading
  // of the ternary chain below. `isCrashGame` is unchanged — six other places
  // read it.
  const boardKind = boardFor({
    gameType: currentGame.track.gameType,
    bettingMode: gameConfig.bettingMode
  })
  // Birdie carries no recent-form rail button (owner 2026-09-17: the overlay
  // "does not really make sense" on this board). `<ActionsPane>` renders the
  // button only when it is given a handler, so withholding one here drops it on
  // BOTH mounts — mobile and desktop — and leaves the other games' rail
  // untouched. The popover below stays mounted behind `isStatsOpen`, which can
  // no longer become true here; Birdie's own history sidebar is unaffected.
  const showRecentForm = boardKind !== 'birdie'

  return (
    <>
      {showOrientationMessage && <OrientationMessage />}
      <div
        className="marbles-race_outer"
        style={showOrientationMessage ? { display: 'none' } : undefined}
      >
        <div className="marbles-race">
          {/* Birdie's golf-ball gradients, declared FIRST in the document. Every
              Birdie tree re-declares the same fixed ids and the first wins — and
              on desktop the first used to be the phone rail's copy inside the
              `display: none` bottom panel, which no browser paints from, so every
              ball on the page lost its sphere. This copy is never hidden. */}
          {boardKind === 'birdie' && <BirdieBallDefs />}
          <IdleBanner gameId={currentGameId} />
          <RightSidebarContainer game={currentGame} showStatsAndRules />
          <ShowRaceBannerContainer game={currentGame} />
          <GameTopToolbarContainer showLogo={!isMarbleGame(currentGame)} />

          {/* v8: crash past rounds moved into ShowRaceResults (right side,
              like every other game) — the top-center strip is gone so the
              center countdown owns the stage. */}

          {Boolean(isTracksPanelOpen) && <GameTracksPanel isScheduledRace={isScheduledRace} />}

          <div className={styles['marbles-game-show-broadcast']}>
            <div
              className={`${styles['dolby-feed']} ${
                isSocialFocusOpen ? 'dolby-feed--stream-cornered' : ''
              }`}
            >
              {!shouldHideGameInterface &&
              showSocial(currentGame.id, socialBetsGameId, latestEvent, isCrashGame) ? (
                <>
                  <div
                    className={`left-floating-panel${boardKind === 'birdie' ? ' left-floating-panel--birdie' : ''}`}
                    style={rightStyle}
                  >
                    {/* Crash uses the dedicated Aviator-style cashout table (player,
                        cashout multiplier, stake, won/lost payout). Other games keep
                        the marble-oriented top-bets list. */}
                    {isCrashGame ? <SocialBetTable /> : <MarbleBetTable />}
                  </div>
                  <div
                    className={`social-bet-stream${boardKind === 'birdie' ? ' social-bet-stream--birdie' : ''}`}
                  >
                    <AnimatedSocialBetStream />
                  </div>
                </>
              ) : null}

              {!shouldHideGameInterface && (
                <div
                  className={`bottom-floating-panel ${latestEvent ? latestEvent.eventType : ''} ${
                    userHasBet ? 'user-has-bet' : ''
                  }`}
                >
                  {/* v12.4 ask 6: never on crash — the backend hard-rejects
                      free bets AND boosters on crash games (register_bet.go),
                      so a floating inventory pill here would advertise an
                      unplayable reward (the old gate only excluded
                      chip-placement mode, letting a sidebar-activated bonus
                      render a stray pill on crash pages). */}
                  {((!isBettingAllowed && isPlacingNextRaceBet) ||
                    (isBettingAllowed && !nextRaceBet)) &&
                  !userHasBet &&
                  !isCrashGame &&
                  gameConfig.bettingMode !== 'chip-placement' &&
                  (oldestBooster || activeBonus) ? (
                    <div className="floating-inventory-container flex gap-2">
                      {oldestBooster ? (
                        <BoosterItem
                          isMobile
                          reward={`+${
                            oldestBooster.boost.unit === 'percentage'
                              ? `${oldestBooster.boost.value}%`
                              : oldestBooster.boost.value
                          }`}
                        />
                      ) : null}
                      {activeBonus ? (
                        <BonusItem
                          isMobile
                          onClose={() => deactivateItem(activeBonus.id)}
                          reward={`${getCurrencySymbol(activeBonus.currency)} ${displayMoney(activeBonus.amount)}`}
                        />
                      ) : null}
                    </div>
                  ) : null}

                  <MobileBoostIndicator
                    streakType={currentStreak?.streakType}
                    streakBoostPercent={currentStreak?.streakBoostPercent}
                  />
                  {/* B19b — every game's past results, including Birdie's, in
                      the ONE surface that renders them (owner: "if it's this
                      golf birdie game it will show like the design you've made
                      else like it's shown for the other games so we don't
                      reinvent it all"). `<ShowRaceResults>` branches inside:
                      marble winners, crash pills, or Birdie's round discs. This
                      is also the phone's past-rounds row — the same mount every
                      other game uses, so B19's separate phone strip is gone.

                      B16 still holds for the CLOCK: `<ShowRaceStatus>` below
                      stays off for Birdie, whose capsule carries the countdown
                      and the score. */}
                  {/* Birdie's phone band is EMPTY: the rail rides the board's
                      capsule row instead (`railSlot` below) and the bet-history
                      button goes, both for the vertical space (owner
                      2026-09-17). Every other game keeps the band it always
                      had, and Birdie's own desktop mount is the one in the
                      right-hand panel further down, untouched. */}
                  {boardKind !== 'birdie' && (
                    <>
                      <ShowRaceResults
                        marbles={currentGame.marbles}
                        openRightSidebar={openRightSidebar}
                        races={currentGame.races}
                        resultsToSubmit={currentGame.resultsToSubmit}
                        gameType={currentGame.track.gameType}
                      />
                      <ActionsPane
                        isStatsOpen={isStatsOpen}
                        onToggleStats={showRecentForm ? toggleStats : undefined}
                        openRightSidebar={openRightSidebar}
                      />
                    </>
                  )}
                  {boardKind !== 'birdie' && <ShowRaceStatus game={currentGame} />}
                </div>
              )}

              {!shouldHideStream ? (
                <>
                  <LiveStream
                    crashAcquire={isCrashGame}
                    // Birdie's phone band is empty — its rail rides the board's
                    // capsule row — so nothing is reserved under the board and
                    // the stream takes the height back.
                    {...(boardKind === 'birdie' ? { mobileBandPx: 0 } : {})}
                    playoutDelay={playoutDelayForGame(isCrashGame)}
                    fallbackImage={{
                      src: currentGame.previewThumbnail ?? currentGame.track.background,
                      alt: `${currentGame.track.name} Thumbnail`
                    }}
                    streamAccountId={streamAccountId}
                    streamName={streamName}
                    streamSubscriberToken={streamSubscriberToken}
                  />
                  <StreamRoundIntro game={currentGame} />

                  {/* LOCKDOWN edge-HUD chrome over the video (crash games only).
                      Read-only, pointer-events: none — it owns the frame
                      geometry (brackets/sweep/vignette + REC telemetry). */}
                  {isCrashGame && <CrashHudOverlay game={currentGame} />}

                  {/* Birdie: the arc board's overlay target. A plain div the
                      board (mounted in the bottom column) portals into, so the
                      arc sits on the VIDEO box without touching <LiveStream>. */}
                  {boardKind === 'birdie' && <div className={BIRDIE_ARC_SLOT_CLASS} />}

                  {/* THE multiplier — the widget owns the number and all status
                      text now (the video carries no baked game text). Upper-
                      centre, pointer-events: none, one rAF loop, no per-tick
                      React state. */}
                  {isCrashGame && <CrashMultiplierDisplay game={currentGame} />}

                  {/* SocialStream (streamer/caller video) is NOT rendered — the
                      feature was never finished and no `social-streams` payload
                      is sent, so it always returned null anyway. Components are
                      kept for whenever it is picked back up.

                      Removing the render also settles a latent CSS ambiguity:
                      the mobile stream-corner rule matches `.main-video-container`
                      as a DESCENDANT of .dolby-feed, and SocialStream rendered a
                      SECOND one. That was harmless while the corner was
                      crash-gated (crash has no streamer) but became reachable
                      when picture-in-picture went fleet-wide. With only one video
                      container the selector can no longer be ambiguous. */}
                </>
              ) : null}
            </div>
          </div>

          {!shouldHideGameInterface && (
            <>
              <div className="right-floating-panel" style={rightStyle}>
                {/* B16 — the desktop half of the same gate: this IS "the
                    countdown … we show to the right with the past game results"
                    the owner asked to remove for Birdie. The board's capsule
                    carries the clock and the score instead. */}
                {boardKind !== 'birdie' && <ShowRaceStatus game={currentGame} />}
                {/* B19b — Birdie's past rounds come back to the rail every other
                    game already uses, as `<ShowRaceResults>`' own Birdie branch:
                    the discs sit where the crash pills and the marble winners
                    sit, under the same "Past Games" button. B19's geometry-driven
                    column inside the board is gone with the box it needed. */}
                <ShowRaceResults
                  marbles={currentGame.marbles}
                  openRightSidebar={openRightSidebar}
                  races={currentGame.races}
                  resultsToSubmit={currentGame.resultsToSubmit}
                  gameType={currentGame.track.gameType}
                />
                <ActionsPane
                  isStatsOpen={isStatsOpen}
                  onToggleStats={showRecentForm ? toggleStats : undefined}
                  openRightSidebar={openRightSidebar}
                />
              </div>

              {/* Recent form, opened from the action rail. Rendered ONCE here
                  rather than inside ActionsPane, which mounts twice (mobile and
                  desktop) — two instances would portal two popovers.

                  B19b: one popover for every game. Birdie's rows — how often
                  none, one, two and all three putts dropped over the last 50
                  rounds — are `<RoundStats>`' Birdie branch inside it, because
                  the shared panel's own rows are "which marble won", the one
                  number the owner said means nothing on a board that is repriced
                  every round. Same button, same panel, Birdie's vocabulary. */}
              {isStatsOpen && <RoundStatsPopover onClose={() => setIsStatsOpen(false)} />}

              {/* ── Crash stats pills (bottom-left overlay) — same surface as
                  chip-placement: Balance · Total staked while betting /
                  queueing, Balance · In play during a live round with bets.
                  The top-right header balance is hidden for crash games
                  (BalanceContainer). ── */}
              {isAuthenticatedPlayer(currentPlayer) && isCrashGame && (
                <div className="chip-stats-overlay" style={chipStatsStyle}>
                  <div className="chip-stats-overlay__pill">
                    <span className="chip-stats-overlay__label">{t('Balance')}</span>
                    <span className="chip-stats-overlay__divider" aria-hidden />
                    <span className="chip-stats-overlay__value">
                      {currentPlayer.balanceLoaded ? (
                        <>
                          {getCurrencySymbol(balanceCurrency)}
                          {displayAmount(
                            animatedBalance,
                            true,
                            getCurrencyDecimals(balanceTarget, balanceCurrency)
                          )}
                        </>
                      ) : (
                        <span className="balance-skeleton" aria-hidden="true" />
                      )}
                    </span>
                  </div>
                  {crashStats.inPlay > 0 ? (
                    <div className="chip-stats-overlay__pill">
                      <span className="chip-stats-overlay__label">{t('In play')}</span>
                      <span className="chip-stats-overlay__divider" aria-hidden />
                      <span className="chip-stats-overlay__value">
                        {`${getCurrencySymbol(balanceCurrency)}${displayAmount(animatedCrashInPlay, true, getCurrencyDecimals(crashStats.inPlay, balanceCurrency))}`}
                      </span>
                    </div>
                  ) : (
                    <div className="chip-stats-overlay__pill">
                      <span className="chip-stats-overlay__label">{t('Total staked')}</span>
                      <span className="chip-stats-overlay__divider" aria-hidden />
                      <span className="chip-stats-overlay__value">
                        {crashStats.totalStaked > 0 || animatedCrashStaked > 0
                          ? `${getCurrencySymbol(balanceCurrency)}${displayAmount(animatedCrashStaked, true, getCurrencyDecimals(crashStats.totalStaked, balanceCurrency))}`
                          : '0'}
                      </span>
                    </div>
                  )}
                  {/* Potential win (note c): "—" while betting, counts up
                      with the live multiplier while riding, flips to Won
                      when everything banked. The pill drives its own
                      per-frame value off the shared display clock. */}
                  <CrashPotentialPill
                    currency={balanceCurrency}
                    readout={crashStats.potential}
                    payoutCeiling={crashPayoutCeiling}
                    shortLabel={isMobile}
                  />
                </div>
              )}

              {/* ── Chip-placement stats pills (bottom-left overlay) ── */}
              {isAuthenticatedPlayer(currentPlayer) &&
                !isCrashGame &&
                gameConfig.bettingMode === 'chip-placement' && (
                  <div className="chip-stats-overlay" style={chipStatsStyle}>
                    <div className="chip-stats-overlay__pill">
                      <span className="chip-stats-overlay__label">{t('Balance')}</span>
                      <span className="chip-stats-overlay__divider" aria-hidden />
                      <span className="chip-stats-overlay__value">
                        {currentPlayer.balanceLoaded ? (
                          <>
                            {getCurrencySymbol(balanceCurrency)}
                            {displayAmount(
                              animatedBalance,
                              true,
                              getCurrencyDecimals(balanceTarget, balanceCurrency)
                            )}
                          </>
                        ) : (
                          <span className="balance-skeleton" aria-hidden="true" />
                        )}
                      </span>
                    </div>
                    <div className="chip-stats-overlay__pill">
                      <span className="chip-stats-overlay__label">{t('Total Bet')}</span>
                      <span className="chip-stats-overlay__divider" aria-hidden />
                      <span className="chip-stats-overlay__value">
                        {chipStats.chipCount > 0 || animatedTotalStaked > 0
                          ? `${getCurrencySymbol(balanceCurrency)}${displayAmount(animatedTotalStaked, true, getCurrencyDecimals(chipStats.totalStaked, balanceCurrency))}`
                          : '0'}
                      </span>
                    </div>
                    <div className="chip-stats-overlay__pill">
                      <span className="chip-stats-overlay__label">
                        {isMobile ? t('Win') : t('Potential Win')}
                      </span>
                      <span className="chip-stats-overlay__divider" aria-hidden />
                      <span className="chip-stats-overlay__value chip-stats-overlay__value--green">
                        {chipStats.chipCount > 0 || animatedTotalPotentialWin > 0
                          ? `${getCurrencySymbol(balanceCurrency)}${displayAmount(animatedTotalPotentialWin, true, getCurrencyDecimals(chipStats.totalPotentialWin, balanceCurrency))}`
                          : '0'}
                      </span>
                    </div>
                  </div>
                )}

              <div className="marbles-race__bottom" style={bottomStyle}>
                <div className="marbles-race__bottom__info">
                  <ShowRaceStatus game={currentGame} />
                </div>

                {isAuthenticatedPlayer(currentPlayer) ? (
                  // eslint-disable-next-line no-nested-ternary -- one arm per board kind
                  boardKind === 'birdie' ? (
                    <BirdieBoard
                      game={currentGame}
                      onStatsChange={setChipStats}
                      player={currentPlayer}
                      railSlot={
                        <>
                          {/* The flag is this component's own child; its discs
                              are hidden in the row (owner 2026-09-17) and the
                              rounds are read in the sidebar it opens. */}
                          <ShowRaceResults
                            marbles={currentGame.marbles}
                            openRightSidebar={openRightSidebar}
                            races={currentGame.races}
                            resultsToSubmit={currentGame.resultsToSubmit}
                            gameType={currentGame.track.gameType}
                          />
                          {/* Bet history rejoins the flag. No handler for the
                              stats overlay, so that button stays gone. */}
                          <ActionsPane openRightSidebar={openRightSidebar} />
                        </>
                      }
                    />
                  ) : isCrashGame ? (
                    <CrashChipPanel
                      game={currentGame}
                      player={currentPlayer}
                      onStatsChange={setCrashStats}
                    />
                  ) : gameConfig.bettingMode === 'chip-placement' ? (
                    <ChipPlacementPanel
                      game={currentGame}
                      player={currentPlayer}
                      onStatsChange={setChipStats}
                    />
                  ) : (
                    <>
                      <PlaceBetPanel game={currentGame} player={currentPlayer} />
                      <CurrentBetPanelContainer game={currentGame} />
                    </>
                  )
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
